import type {
  TransactionStatus,
  TransactionDirection,
} from "Infrastructure/Api/Api";
import type { FilterOption } from "Components/Shared/Filters/FilterOption";
import { usePersistedFilters } from "Hooks/usePersistedFilters";
import type { TimePeriod } from "Types/TimePeriod";
import type { LocalStorageKey } from "Utils/Storage/LocalStorage/LocalStorageKeys";

export type { TimePeriod } from "Types/TimePeriod";
const EMPTY_FILTERS: TransactionFilters = {};

export type TransactionFilters = {
  timePeriod?: TimePeriod;
  dateFrom?: Date;
  dateTo?: Date;
  payPoint?: FilterOption;
  employee?: FilterOption;
  status?: TransactionStatus;
  direction?: TransactionDirection;
  isRefundOrCancellation?: boolean;
  searchPublicID?: string;
};

const INITIAL_FILTERS: TransactionFilters = {
  timePeriod: "month",
};

// Configuration types for available filters
export type AvailableFilters = {
  timePeriod: boolean;
  payPoint: boolean;
  employee: boolean;
  status: boolean;
  direction: boolean;
  isRefundOrCancellation: boolean;
};

type FilterVariant = "customer" | "merchant";

const AVAILABLE_FILTERS_BY_VARIANT: Record<FilterVariant, AvailableFilters> = {
  customer: {
    timePeriod: true,
    payPoint: false,
    employee: false,
    status: true,
    direction: true,
    isRefundOrCancellation: true,
  },
  merchant: {
    timePeriod: true,
    payPoint: true,
    employee: true,
    status: true,
    direction: true,
    isRefundOrCancellation: true,
  },
};

type FilterManagerConfig = {
  variant: FilterVariant;
  storageKey: LocalStorageKey;
};

export const useTransactionFilterManager = ({
  variant,
  storageKey,
}: FilterManagerConfig) => {
  const { filters, setFilters, applyFilters } =
    usePersistedFilters<TransactionFilters>({
      storageKey,
      initialFilters: INITIAL_FILTERS,
    });

  const resetFilters = () => {
    applyFilters(EMPTY_FILTERS);
  };

  const setSearchPublicID = (searchPublicID?: string) => {
    setFilters((prev) => ({ ...prev, searchPublicID }));
  };

  const availableFilters = AVAILABLE_FILTERS_BY_VARIANT[variant];
  const activeFilterCount = [
    availableFilters.timePeriod && filters.timePeriod !== undefined, // Don't count when undefined ("Neomezeno")
    availableFilters.payPoint && filters.payPoint,
    availableFilters.employee && filters.employee,
    availableFilters.status && filters.status,
    availableFilters.direction && filters.direction,
    availableFilters.isRefundOrCancellation &&
      filters.isRefundOrCancellation === true,
  ].filter(Boolean).length;

  return {
    filters,
    availableFilters,
    searchPublicID: filters.searchPublicID,
    activeFilterCount,
    applyFilters,
    resetFilters,
    setSearchPublicID,
  };
};

export type TransactionFilterManager = ReturnType<
  typeof useTransactionFilterManager
>;
